import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import { alpha } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { RefreshIcon } from 'assets/icons';
import CreateTicketDialog from 'components/common/CreateTicketDialog';
import type { DateRangeValue, FilterOption, MasterFilterValues } from 'components/common/MasterFilter';
import MasterFilter, { useMasterFilter } from 'components/common/MasterFilter';
import PaginationFooter from 'components/common/PaginationFooter';
import { useBusinessUnits } from 'contexts/BusinessUnitsContext';
import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import type { RootState } from 'redux/store';
import { useGetTicketFilterUsersQuery, useGetTicketsQuery } from 'services/api/tickets.api';
import {
  TICKET_MOTIVE_LABELS,
  TICKET_MOTIVES,
  TICKET_URGENCY_LABELS,
  type TicketMotive,
  type TicketStatusFilter,
  type TicketUrgency,
} from 'services/types/ticket';
import {
  META_BLUE,
  NAVY,
  RADIUS_CARD,
  RADIUS_COIN,
  RADIUS_ITEM,
  SHADOW,
  STATUS_WARN,
  SURFACE,
  SURFACE_CONTAINER_LOWEST,
  SURFACE_HOVER,
  YELLOW,
} from 'styles/tokens';

import TicketStatusChip from '../Ticket/components/TicketStatusChip';
import { buildTicketsFilterConfig } from './ticketsFilterFields';

const FONT = 'Jost, sans-serif';

const toUserOptions = (users: { id: number; name: string }[] | undefined, currentUserId?: number): FilterOption[] =>
  (users ?? [])
    .map((u) => ({ value: String(u.id), label: u.id === currentUserId ? `${u.name} (yo)` : u.name }))
    .sort((a, b) => a.label.localeCompare(b.label));

const PAGE_PARAM = 'tickets.page';
const PAGE_SIZE_PARAM = 'tickets.pageSize';
const DEFAULT_PAGE_SIZE = 10;

const readPaginationFromUrl = (): { page: number; pageSize: number } => {
  if (typeof window === 'undefined') return { page: 0, pageSize: DEFAULT_PAGE_SIZE };
  const params = new URLSearchParams(window.location.search);
  const pageRaw = Number(params.get(PAGE_PARAM));
  const sizeRaw = Number(params.get(PAGE_SIZE_PARAM));
  return {
    page: Number.isFinite(pageRaw) && pageRaw >= 0 ? pageRaw : 0,
    pageSize: Number.isFinite(sizeRaw) && sizeRaw > 0 ? sizeRaw : DEFAULT_PAGE_SIZE,
  };
};

const primaryBtnSx = {
  bgcolor: YELLOW,
  color: NAVY,
  borderRadius: RADIUS_COIN,
  textTransform: 'none' as const,
  fontFamily: FONT,
  fontWeight: 600,
  boxShadow: 'none',
  px: 2.5,
  '&:hover': { bgcolor: alpha(YELLOW, 0.85), boxShadow: 'none' },
};

type TicketCategory = 'all' | 'withBusinessUnit' | 'rollbar' | 'other';

type AppliedFilters = {
  includeAll: boolean;
  statuses?: TicketStatusFilter[];
  businessUnitIds?: number[];
  createdByUserIds?: number[];
  interestedUserIds?: number[];
  category?: TicketCategory;
  urgencies?: TicketUrgency[];
  motives?: TicketMotive[];
  deadlineFrom?: string;
  deadlineTo?: string;
  createdFrom?: string;
  createdTo?: string;
};

const toNumberArray = (v: unknown): number[] | undefined => {
  if (!Array.isArray(v) || v.length === 0) return undefined;
  const nums = v.map((x) => Number(x)).filter((n) => !Number.isNaN(n));
  return nums.length > 0 ? nums : undefined;
};

const VALID_CATEGORIES: readonly TicketCategory[] = ['all', 'withBusinessUnit', 'rollbar', 'other'];
const toCategory = (v: unknown): TicketCategory | undefined =>
  typeof v === 'string' && (VALID_CATEGORIES as readonly string[]).includes(v) ? (v as TicketCategory) : undefined;

const VALID_URGENCIES: readonly TicketUrgency[] = ['LOW', 'MEDIUM', 'HIGH'];
const toUrgencies = (v: unknown): TicketUrgency[] | undefined => {
  if (!Array.isArray(v) || v.length === 0) return undefined;
  const filtered = v.filter((x): x is TicketUrgency => (VALID_URGENCIES as readonly string[]).includes(x as string));
  return filtered.length > 0 ? filtered : undefined;
};

const VALID_MOTIVES: readonly TicketMotive[] = TICKET_MOTIVES;
const toMotives = (v: unknown): TicketMotive[] | undefined => {
  if (!Array.isArray(v) || v.length === 0) return undefined;
  const filtered = v.filter((x): x is TicketMotive => (VALID_MOTIVES as readonly string[]).includes(x as string));
  return filtered.length > 0 ? filtered : undefined;
};

const toDateRange = (v: unknown): { from?: string; to?: string } => {
  const dr = v as DateRangeValue | undefined;
  return {
    from: dr?.start?.isValid() ? dr.start.toISOString() : undefined,
    to: dr?.end?.isValid() ? dr.end.toISOString() : undefined,
  };
};

const deriveApplied = (values: MasterFilterValues): AppliedFilters => {
  const statusesRaw = values.statuses as string[] | undefined;
  const deadline = toDateRange(values.deadlineRange);
  const created = toDateRange(values.createdRange);
  return {
    includeAll: true,
    statuses: statusesRaw && statusesRaw.length > 0 ? (statusesRaw as TicketStatusFilter[]) : undefined,
    businessUnitIds: toNumberArray(values.businessUnitIds),
    createdByUserIds: toNumberArray(values.createdByUserIds),
    interestedUserIds: toNumberArray(values.interestedUserIds),
    category: toCategory(values.category),
    urgencies: toUrgencies(values.urgencies),
    motives: toMotives(values.motives),
    deadlineFrom: deadline.from,
    deadlineTo: deadline.to,
    createdFrom: created.from,
    createdTo: created.to,
  };
};

const Tickets: React.FC = () => {
  const navigate = useNavigate();

  const user = useSelector((state: RootState) => state.user);
  const isAdmin = !!user?.isAdmin;

  const filterConfig = useMemo(() => buildTicketsFilterConfig(isAdmin), [isAdmin]);

  const { businessUnits } = useBusinessUnits();
  const { data: filterUsers } = useGetTicketFilterUsersQuery();

  const businessUnitOptions = useMemo<FilterOption[]>(() => {
    const list = (businessUnits ?? []).map((bu) => ({
      value: String(bu.id),
      label: bu.name,
      group: bu.company?.name ?? undefined,
    }));
    list.sort((a, b) => {
      const byGroup = (a.group ?? '').localeCompare(b.group ?? '');
      if (byGroup !== 0) return byGroup;
      return a.label.localeCompare(b.label);
    });
    return list;
  }, [businessUnits]);

  const creatorOptions = useMemo<FilterOption[]>(
    () => toUserOptions(filterUsers?.creators, user?.id),
    [filterUsers?.creators, user?.id]
  );
  const interestedOptions = useMemo<FilterOption[]>(
    () => toUserOptions(filterUsers?.interested, user?.id),
    [filterUsers?.interested, user?.id]
  );

  // Map de id -> nombre para resolver los ADMINS asignados (interesados) de cada ticket.
  // Solo se incluyen usuarios admin; los no-admin no generan chip.
  const adminNameById = useMemo(() => {
    const map = new Map<number, string>();
    [...(filterUsers?.interested ?? []), ...(filterUsers?.creators ?? [])].forEach((u) => {
      if (u.isAdmin && !map.has(u.id)) map.set(u.id, u.name);
    });
    return map;
  }, [filterUsers?.interested, filterUsers?.creators]);

  const dynamicOptions = useMemo<Record<string, FilterOption[]>>(
    () => ({
      businessUnitIds: businessUnitOptions,
      createdByUserIds: creatorOptions,
      interestedUserIds: interestedOptions,
    }),
    [businessUnitOptions, creatorOptions, interestedOptions]
  );

  const filter = useMasterFilter(filterConfig, null, dynamicOptions, undefined, {
    urlNamespace: 'tickets',
  });

  const appliedFilters = useMemo<AppliedFilters>(() => deriveApplied(filter.values), [filter.values]);
  const [paginationModel, setPaginationModel] = useState(() => readPaginationFromUrl());

  // Persist pagination to URL so navigation back to Tickets restores state.
  // Reads `window.location.search` directly to avoid clobbering filter params written by
  // `useMasterFilter`'s URL persistence in the same render pass.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const before = params.toString();
    if (paginationModel.page === 0) params.delete(PAGE_PARAM);
    else params.set(PAGE_PARAM, String(paginationModel.page));
    if (paginationModel.pageSize === DEFAULT_PAGE_SIZE) params.delete(PAGE_SIZE_PARAM);
    else params.set(PAGE_SIZE_PARAM, String(paginationModel.pageSize));
    const after = params.toString();
    if (before === after) return;
    navigate({ pathname: window.location.pathname, search: after ? `?${after}` : '' }, { replace: true });
  }, [paginationModel, navigate]);

  const { page, pageSize } = paginationModel;

  // Reset to first page whenever the applied filters change.
  const prevAppliedRef = useRef(appliedFilters);
  useEffect(() => {
    if (prevAppliedRef.current === appliedFilters) return;
    prevAppliedRef.current = appliedFilters;
    setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
  }, [appliedFilters]);

  const { data, isLoading, isFetching, refetch } = useGetTicketsQuery({
    ...appliedFilters,
    page: page + 1,
    pageSize,
  });

  const [openCreateDialog, setOpenCreateDialog] = useState(false);

  const handleTicketCreated = () => {
    refetch().catch((error) => {
      console.error('Error refetching tickets:', error);
    });
  };

  const handleViewTicket = (ticketId: number) => {
    navigate(`/tickets/${ticketId}`);
  };

  const total = data?.total || 0;
  const filteredTickets = data?.tickets || [];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: SURFACE, px: { xs: 2, md: 3 }, pt: 2, pb: 6 }}>
      <Box sx={{ mb: 2 }}>
        <MasterFilter {...filter} dynamicOptions={dynamicOptions} />
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
          mb: 2,
        }}
      >
        <Button startIcon={<AddIcon />} onClick={() => setOpenCreateDialog(true)} sx={primaryBtnSx}>
          Nuevo Ticket
        </Button>
        <Tooltip title="Actualizar lista" arrow>
          <IconButton
            onClick={isFetching ? undefined : () => refetch()}
            disabled={isFetching}
            size="small"
            aria-label="Actualizar lista"
            sx={{
              color: alpha(NAVY, 0.55),
              transition: 'all 0.15s ease-in-out',
              '&:hover': { bgcolor: alpha(NAVY, 0.06) },
            }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
          <Typography sx={{ fontFamily: FONT, color: `${NAVY}99` }}>Cargando tickets...</Typography>
        </Box>
      ) : filteredTickets && filteredTickets.length > 0 ? (
        <>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {filteredTickets.map((ticket) => {
              const isPending = ticket.status === 'PENDING';
              return (
                <Box
                  key={ticket.id}
                  onClick={() => handleViewTicket(ticket.id)}
                  sx={{
                    bgcolor: SURFACE_CONTAINER_LOWEST,
                    borderRadius: RADIUS_CARD,
                    px: 2,
                    py: 1.5,
                    boxShadow: SHADOW,
                    borderLeft: isPending ? `3px solid ${YELLOW}` : '3px solid transparent',
                    display: 'flex',
                    alignItems: 'stretch',
                    justifyContent: 'space-between',
                    flexWrap: 'nowrap',
                    minHeight: 64,
                    gap: 2,
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease, background-color 0.15s ease',
                    '&:hover': {
                      bgcolor: SURFACE_HOVER,
                      transform: 'translateY(-2px)',
                      boxShadow: `0px 16px 40px ${alpha(NAVY, 0.08)}`,
                    },
                  }}
                >
                  <Box
                    sx={{
                      flex: 4,
                      minWidth: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      justifyContent: 'center',
                      gap: 0.25,
                    }}
                  >
                    <Typography sx={{ fontFamily: FONT, fontSize: '0.7rem', color: `${NAVY}99` }}>
                      Por: {ticket.user?.name || 'Automático'}
                    </Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: '0.7rem', color: `${NAVY}77` }}>
                      {ticket.businessUnit?.name || 'General'}
                    </Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: '0.7rem', color: `${NAVY}77` }}>
                      {new Date(ticket.createdAt).toLocaleString('es-CL')}
                    </Typography>
                    {ticket.motive && (
                      <Box
                        sx={{
                          mt: 0.25,
                          alignSelf: 'flex-start',
                          bgcolor: alpha(NAVY, 0.06),
                          color: NAVY,
                          fontFamily: FONT,
                          fontSize: '0.55rem',
                          fontWeight: 700,
                          letterSpacing: '0.03em',
                          textTransform: 'uppercase',
                          px: 0.5,
                          py: 0,
                          borderRadius: RADIUS_ITEM,
                        }}
                      >
                        {TICKET_MOTIVE_LABELS[ticket.motive]}
                      </Box>
                    )}
                  </Box>
                  <Box
                    sx={{
                      flex: 7,
                      minWidth: 0,
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      gap: 0.25,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: FONT,
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        color: NAVY,
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: 400,
                      }}
                    >
                      {ticket.title}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: FONT,
                        fontSize: '0.75rem',
                        color: `${NAVY}99`,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: 500,
                      }}
                    >
                      {ticket.description}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      flex: 3,
                      minWidth: 0,
                      px: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                    }}
                  >
                    {ticket.urgency && (
                      <Box
                        sx={{
                          bgcolor:
                            ticket.urgency === 'HIGH'
                              ? alpha(STATUS_WARN, 0.12)
                              : ticket.urgency === 'MEDIUM'
                                ? alpha('#A8810A', 0.1)
                                : alpha(NAVY, 0.06),
                          color:
                            ticket.urgency === 'HIGH'
                              ? STATUS_WARN
                              : ticket.urgency === 'MEDIUM'
                                ? '#8A6708'
                                : `${NAVY}99`,
                          fontFamily: FONT,
                          fontSize: '0.8rem',
                          fontWeight: 700,
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase',
                          px: 1.5,
                          py: 0.625,
                          borderRadius: RADIUS_ITEM,
                        }}
                      >
                        Urgencia {TICKET_URGENCY_LABELS[ticket.urgency]}
                      </Box>
                    )}
                  </Box>
                  <Box
                    sx={{
                      flex: 5,
                      minWidth: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      justifyContent: 'center',
                      gap: 0.5,
                      ml: 2,
                    }}
                  >
                    <TicketStatusChip
                      status={ticket.status}
                      size="small"
                      lastMessageFromCurrentUser={(ticket.lastMessageWasSentByAdminUser ?? false) === !!user?.isAdmin}
                    />
                    {(ticket.interestedUserIds ?? [])
                      .map((id) => adminNameById.get(id))
                      .filter((name): name is string => Boolean(name))
                      .map((name, idx) => (
                        <Box
                          key={`assigned-${ticket.id}-${idx + 1}-${name}`}
                          sx={{
                            bgcolor: alpha(META_BLUE, 0.12),
                            color: META_BLUE,
                            fontFamily: FONT,
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            letterSpacing: '0.03em',
                            textTransform: 'uppercase',
                            px: 1,
                            py: 0.25,
                            borderRadius: RADIUS_ITEM,
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {name}
                        </Box>
                      ))}
                  </Box>
                </Box>
              );
            })}
          </Box>
          <PaginationFooter
            page={paginationModel.page}
            pageSize={paginationModel.pageSize}
            rowCount={total}
            onPageChange={(newPage) => setPaginationModel((prev) => ({ ...prev, page: newPage }))}
            onPageSizeChange={(newPageSize) =>
              setPaginationModel((prev) => ({ ...prev, pageSize: newPageSize, page: 0 }))
            }
            sx={{ mt: 2 }}
          />
        </>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '300px',
            gap: 2,
          }}
        >
          <Typography sx={{ fontFamily: FONT, fontSize: '1rem', color: `${NAVY}99` }}>No hay tickets 🎉</Typography>
        </Box>
      )}
      <CreateTicketDialog
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        isAdmin={isAdmin}
        onSuccess={handleTicketCreated}
      />
    </Box>
  );
};

export default Tickets;
